import { type AxiosPromise } from "axios";
import classNames from "classnames";
import React from "react";
import { Modal } from "../../components";
import RelatedFilesWithUpload from "../../components/RelatedFiles/RelatedFilesWithUpload";
import RemovableLabel from "components/Atoms/RemovableLabel";
import { Checkbox } from "components/Atoms/inputs/Checkbox";

interface Props {
  selectedFiles: server.dto.File[]
  onFilesSelected: (newFiles: server.dto.File[]) => void
  relatedFilesApi: () => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>
  uploadFilesApi: (files, config) => AxiosPromise<server.dto.ApiResponse>

  downloadLinkInEmail: boolean
  downloadLinkInEmailToggled: () => void

  attachFilesToEmail: boolean
  attachFilesToEmailToggled: () => void

  config: server.dto.LinkedEmailPanelConfig

  content: string

  attachmentsRequired: boolean
  showErrors: boolean

  hasNoAttachmentsError: boolean
  noDownloadOptionSelected: boolean
  selectedDownloadLinkButHasNoLink: boolean
}

const AttachFiles = (props: Props) => {
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const mountRef = React.useRef(false);

  React.useEffect(() => {
    if (mountRef.current) {
      if (props.attachmentsRequired) {
        setIsModalOpen(true);
      }
    } else {
      mountRef.current = true;
    }
  }, [props.attachmentsRequired]);

  const onFileRemoved = (file: server.dto.File) => {
    const newFiles = props.selectedFiles.filter(f => f.id !== file.id);
    props.onFilesSelected(newFiles);
  };

  const onFilesAttached = (files: server.dto.File[]) => {
    setIsModalOpen(false);
    props.onFilesSelected(files);
  };

  let error = "";

  const hasError = props.showErrors && (props.hasNoAttachmentsError || props.noDownloadOptionSelected || props.selectedDownloadLinkButHasNoLink);

  if (hasError) {
    if (props.hasNoAttachmentsError) {
      error = RESX.File.DidYouMeanToSelectFiles;
    } else if (props.noDownloadOptionSelected) {
      error = RESX.File.SelectOneFileOption;
    } else if (props.selectedDownloadLinkButHasNoLink) {
      error = RESX.File.DownloadLinkMissing;
    }
  }

  const containerClasses = classNames("margin-top", {
    s1_warningborder: hasError
  });

  const totalSize = props.selectedFiles.reduce((total, curr) => total + curr.contentLength, 0);
  const disableAttachFilesToEmail = totalSize > serverReference.constants.emailMessage.maxAttachedBytes;

  return <>
    <Modal
      isOpen={isModalOpen}
      handleClose={() => { setIsModalOpen(false); }}
      title={RESX.File.AttachFiles}
      width="small"
      height={700}
    >
      <RelatedFilesWithUpload
        onFilesSelected={onFilesAttached}
        relatedFilesApi={props.relatedFilesApi}
        uploadFilesApi={props.uploadFilesApi}
        initialSelectedFiles={props.selectedFiles}
      />
    </Modal>

    <div className={containerClasses} id="attachFiles">
      <div style={{ marginBottom: 5 }} id="attachLink">
        <a onClick={() => { setIsModalOpen(true); }}>
          <span className="margin-right-small s1_gray"><i className="icon-paperclip mini" /></span>
          {RESX.File.AttachFiles}:
        </a>
        <span style={{ color: "red", marginLeft: 5, float: "right", display: hasError ? undefined : "none" }}>
          {error}
        </span>
      </div>
      <div className="removableLabel-list">
        {props.selectedFiles.length === 0 && RESX.File.NoFilesAttached}
        {props.selectedFiles.length > 0 && props.selectedFiles.map(file => (
          <RemovableLabel onClick={() => { onFileRemoved(file); }} key={file.id}>
            {file.fullName} ({file.contentLengthText})
          </RemovableLabel>
        ))}
      </div>
      {props.selectedFiles.length > 0 && <>
        <div className="margin-top-small">
          <Checkbox
            checked={props.downloadLinkInEmail}
            onChange={props.downloadLinkInEmailToggled}
            label={RESX.Email.DownloadLinkInEmail}
          />
        </div>
        <div className="margin-top-small">
          <Checkbox
            disabled={disableAttachFilesToEmail}
            checked={props.attachFilesToEmail}
            onChange={props.attachFilesToEmailToggled}
            label={props.config.attachFilesToEmailLabel}
          />
        </div>
      </>}
    </div>
  </>;
};

export default AttachFiles;